"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { zones, getZoneById } from "@/lib/zones"
import type { DailyEntry } from "@/lib/db/schema"
import { Loader2 } from "lucide-react"

interface EditEntryDialogProps {
  entry: DailyEntry
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (entryId: string, zoneId: string, note: string | null) => Promise<void>
}

export function EditEntryDialog({
  entry,
  open,
  onOpenChange,
  onSave,
}: EditEntryDialogProps) {
  const [selectedZoneId, setSelectedZoneId] = useState(entry.zoneId)
  const [note, setNote] = useState(entry.note || "")
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(entry.id, selectedZoneId, note || null)
      onOpenChange(false)
    } catch (error) {
      console.error("Error saving entry:", error)
    } finally {
      setSaving(false)
    }
  }

  const selectedZone = getZoneById(selectedZoneId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upravit záznam</DialogTitle>
          <DialogDescription>
            Datum: {new Date(entry.date).toLocaleDateString("cs-CZ", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Zone Selector */}
          <div className="space-y-3">
            <Label>Zóna</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {zones.map((zone) => {
                const isSelected = zone.id === selectedZoneId
                return (
                  <button
                    key={zone.id}
                    type="button"
                    onClick={() => setSelectedZoneId(zone.id)}
                    className={`
                      relative p-3 rounded-lg border-2 text-left transition-all
                      ${
                        isSelected
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border hover:border-primary/50"
                      }
                    `}
                    style={{
                      borderLeftWidth: "4px",
                      borderLeftColor: isSelected ? zone.color : undefined,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{zone.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {zone.number}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {zone.name.split(" / ")[0]}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected Zone Info */}
          {selectedZone && (
            <div
              className="p-4 rounded-lg border-l-4"
              style={{ borderLeftColor: selectedZone.color }}
            >
              <div className="flex items-start gap-3">
                <span className="text-4xl">{selectedZone.emoji}</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{selectedZone.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedZone.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Poznámka (volitelné)</Label>
            <Textarea
              id="note"
              placeholder="Přidej poznámku k tomuto dni..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Zrušit
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ukládám...
              </>
            ) : (
              "Uložit změny"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
